import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import CircularProgress from '@mui/material/CircularProgress';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import styled from '@emotion/styled';

import { useMst } from '../../models/rootModel';
import { useAnalytics } from '../../utils/analytics';

import { getColor } from '../common/UICommon';

import { AnalyticsDataProps, BaseStep, StepProps, TitleAndDescriptionProps, StepWizardProps } from '../common/BaseStep';
import { StepWrapperBox } from '../styledComponent/Boxes.style';
import { LoaderDiv } from '../styledComponent/Boxes.style';
import { ErrorInputText, Label } from '../styledComponent/SignUpComponents.style';

export const StyledDatePicker = styled(DatePicker)`
  font-family: 'Poppins';
  width: 100%;
  border-radius: 4px;
  outline: none;
  background-color: #f3f4f6;
  .MuiInputBase-input {
    color: ${getColor('BLACK')};
  }
  .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

interface AgeStepProps extends TitleAndDescriptionProps, StepWizardProps, StepProps, AnalyticsDataProps {
  criteriaType: string;
  isDiscoveryFinished?: boolean;
  nextStepId?: string;
}

export const AgeStep = observer((props: AgeStepProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [dateError, setDateError] = useState('');

  const { criteriaType, nextStep, isDiscoveryFinished, goToNamedStep, isActive, nextStepId } = props;
  const { Criteria, UserDiscoveryResponse, Step } = useMst();
  const analytics = useAnalytics();

  useEffect(() => {
    if (isActive) {
      return window.scrollTo(0, 0);
    }
  }, [isActive]);

  const getNextStep = () => {
    if (!nextStepId) return nextStep();
    goToNamedStep(nextStepId);
  };

  const changeButtonFunction = () => {
    if (!date) return;

    analytics.track(`age set ${criteriaType}`, {
      selectedValue: date.toDate(),
      stepId: props.id,
      stepType: props.type,
      stepIndex: props.stepIndex,
      stepTitle: props.title,
      ...props?.analytics,
      ...UserDiscoveryResponse.utms,
      origin: Step.origin,
      userID: UserDiscoveryResponse.accountId,
    });

    // add the date of birth to rediscovery
    UserDiscoveryResponse.setDateOfBirth(date.toDate());

    if (isDiscoveryFinished) {
      const discoveryObj = {
        discovery: {
          byOrigin: true,
          origin: Step.origin,
          ...UserDiscoveryResponse.userAnswersObject,
        },
        ...UserDiscoveryResponse.userBirthDayAndGender,
      };

      if (Step.isReDiscovery) {
        UserDiscoveryResponse.userReDiscovery(discoveryObj);
        return;
      }
      UserDiscoveryResponse.updateDiscoveryDone(discoveryObj);
    }

    getNextStep();
  };

  const checkAge = (newDate: Dayjs) => {
    if (!newDate) {
      return;
    }

    const age = dayjs().year() - newDate.year();
    if (age < 18) {
      setIsButtonDisabled(true);
      setDateError('You must be 18 or older to continue');
      return;
    }
    setIsButtonDisabled(false);
  };

  const changeDate = (newValue: unknown) => {
    if (dateError.length > 0) setDateError('');
    const newDate = newValue as Dayjs;
    if (!dayjs(newDate).isValid()) {
      setDateError('Date not valid');
      return;
    }

    setDate(newDate);
    checkAge(newDate);
  };

  const closeDatePicker = () => {
    if (!date || !dayjs(date).isValid()) {
      setDateError('Date not valid');
      return;
    }
    checkAge(date);
  };

  return (
    <>
      {isActive && Criteria.criterias.length > 0 ? (
        <BaseStep {...props} isButtonDisabled={isButtonDisabled} changeButtonFunction={changeButtonFunction}>
          <StepWrapperBox>
            <Label>Date of birth</Label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                value={date}
                format="LL"
                sx={{ border: 'none' }}
                onChange={(newValue) => changeDate(newValue)}
                onClose={closeDatePicker}
              />
            </LocalizationProvider>

            {dateError.length > 0 ? <ErrorInputText>{dateError}</ErrorInputText> : ''}
          </StepWrapperBox>
        </BaseStep>
      ) : (
        <LoaderDiv>
          <CircularProgress />
        </LoaderDiv>
      )}
    </>
  );
});
